import React, {
  useContext,
  useEffect,
  useState
} from 'react';
import {
  flowRight,
  get,
  isEmpty
} from 'lodash';
import moment from 'moment';
import {
  ELSCommonUIConstants,
  ELSLoggingService,
  ELSPageLoader,
  ELSStorageHelper,
  ELSTokenHelper,
  ELSTokenServiceRegistrar
} from '@els/els-ui-common-react';
import { useHistory } from 'react-router';
import {
  RoutePath
} from '../../components/app/app.constants';
import {
  AppAction,
  Application
} from '../../apis/eols-app-link/eols-app-link.constants';
import {
  AppLinkCookies,
  AppLinkData
} from '../../apis/eols-app-link/eols-app-link.dtos';
import {
  AppLinkRedirectErrorMessage,
  AppLinkRedirectErrorMessages,
  LINK_HASH_QUERY_PARAM
} from './app-link-redirect.constants';
import { ELSTokenUser } from '../../models/els.dtos';
import {
  SparkAppLinkContextDto,
  ChatContextTypeDto
} from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import { AppContext } from '../../components/app/AppProvider.component';
import { getQueryParam } from '../../utilities/location.utilities';
import { NewRelicAnalyticsAction } from '../../models/new-relic-analytics.models';
import {
  fetchAllAppFeatureFlagsAction,
  fetchAppLinkDataAction,
  fetchCourseSectionAction,
  fetchEvolveUsersAction,
  fetchHashLinkAction,
  fetchIpAddressAction,
  trackNewRelicAction
} from '../../components/app/app-state-actions.utilities';
import { AppState } from '../../components/app/app-state.types';
import { addSearchParams } from '../../utilities/app.utilities';
import { DefaultAppState } from '../../components/app/app-state.constants';
import { isOsmosisAI, getChatModeTypeLocalStorageKey } from '../ai-chat/ai-chat.utilities';
import { ChatModeType } from '../ai-chat/ai-chat.constants';
import withHTMLHeadSEO from '../../hocs/with-html-head-seo/withHTMLHeadSEO.hoc';

export type AppLinkRedirectState = {
  hasError: boolean;
  errorMessage: string;
}

const defaultState: AppLinkRedirectState = {
  hasError: false,
  errorMessage: ''
};

const fileName = 'AppLinkRedirect.page';

interface AppLinkMethodProps {
  token: string;
  linkId: string;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const AppLinkRedirectComponent = (): JSX.Element => {

  const [state, setState] = useState<AppLinkRedirectState>(defaultState);
  const history = useHistory();

  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);

  const logError = (errorType: AppLinkRedirectErrorMessage, error): void => {
    const url = get(error, 'config.url', '');
    const method = get(error, 'config.method', '');
    const status = get(error, 'response.status', '');
    const errorMessage = AppLinkRedirectErrorMessages[errorType];

    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.APP_LINK_ERROR,
        props: {
          type: errorType,
          url,
          method,
          status
        }
      }
    });
    ELSLoggingService.error(fileName, errorMessage);
    setState({
      hasError: true,
      errorMessage
    });
  };

  const handleAppLinkLaunch = (appLinkData: AppLinkData) => {

    if (
      appLinkData.action === AppAction.LAUNCH
      && appLinkData.outPostBody?.targetPage === 'CHATBOT_ADMIN'
    ) {
      history.replace(addSearchParams(RoutePath.AI_CHAT_ADMIN, {
        entryId: appLinkData.outPostBody.entryId,
      }));
      return;
    }

    if (
      appLinkData.action === AppAction.LAUNCH
      && appLinkData.outPostBody?.targetPage === 'AUTH'
    ) {
      history.replace(RoutePath.AUTHORING_POC);
      return;
    }

    if (appLinkData.action === AppAction.LAUNCH) {
      const { outPostBody } = appLinkData;
      history.replace(addSearchParams(RoutePath.AI_CHAT, {
        isAppLinkQuery: outPostBody?.query ? true : null,
        isAppLinkAdminOverride: outPostBody?.adminOverride ? true : null,
        adminOverride: outPostBody?.adminOverride ? true : null,
        landOnNewChat: outPostBody?.landOnNewChat ? true : null
      }));
    }
  };

  const handleAppLinkReturn = () => {
    history.replace(RoutePath.AI_CHAT);
    return Promise.resolve();
  };

  const launchApp = (config: AppLinkMethodProps) => {

    const { token, linkId } = config;

    ELSTokenServiceRegistrar.register(token);
    const tokenUser: ELSTokenUser = ELSTokenHelper.getUser();
    const courseSectionId: string = tokenUser.appParams?.courseId?.toString() || null;
    const ecrId: string = tokenUser.appParams?.ecrId?.toString() || null;

    setAppState((prevState: AppState): AppState => {
      return {
        ...prevState,
        registeredToken: token,
        userId: tokenUser.userId.toString(),
        userRole: tokenUser.role,
        courseSectionId,
        tokenIsbns: tokenUser.appParams.isbns,
        ecrId
      };
    });

    fetchEvolveUsersAction({
      useCache: false,
      userId: tokenUser.userId.toString(),
      appStateRef,
      setAppState
    });

    fetchIpAddressAction({ appState, appStateRef, setAppState });

    return fetchAppLinkDataAction({
      appLinkId: linkId,
      appStateRef,
      setAppState,
    }).then((appLinkData: AppLinkData) => {

      if (isOsmosisAI(appLinkData)) {
        window.isHideForsta = true
      }

      // Spark app-links require a contextId in the app-link context.
      const context = appLinkData?.outPostBody?.context;
      if (context?.contextType === ChatContextTypeDto.SPARK) {
        const sparkContext = context as SparkAppLinkContextDto;
        if (!sparkContext.contextId) {
          logError(AppLinkRedirectErrorMessage.MISSING_CONTEXT_ID, null);
          return Promise.resolve();
        }
      }

      return Promise.all([
        fetchAllAppFeatureFlagsAction({
          appStateRef,
          setAppState,
          useCache: false
        }),
        fetchCourseSectionAction({
          courseSectionId,
          appStateRef,
          setAppState,
          useCache: false
        })
      ])
        .then(() => {

          trackNewRelicAction({
            actionProps: {
              action: NewRelicAnalyticsAction.APP_LINK_LAUNCH,
              props: {
                linkType: appLinkData.action,
                srcApp: appLinkData.srcApp,
                targetApp: appLinkData.targetApp
              }
            },
            appStateRef,
          });

          const storageKey = getChatModeTypeLocalStorageKey(appLinkData, tokenUser.userId.toString());
          if (storageKey) {
            const persistedChatModeType = ELSStorageHelper
              .driver(ELSCommonUIConstants.security.Locker.DefaultDriver)
              .namespace('')
              .get(storageKey) as ChatModeType;
            if (persistedChatModeType) {
              setAppState((prevState) => ({ ...prevState, chatModeType: persistedChatModeType }));
            }
          }

          if (appLinkData.targetApp === Application.NHE_AI) {
            handleAppLinkLaunch(appLinkData);
            return Promise.resolve();
          }
          if (appLinkData.srcApp === Application.NHE_AI) {
            handleAppLinkReturn();
            return Promise.resolve();
          }
          return Promise.resolve();
        });
    }).catch((error) => {
      logError(AppLinkRedirectErrorMessage.REQUEST_FAILED, error);
    });
  };

  const isRedirectValid = (config: AppLinkMethodProps): boolean => {
    if (!config) {
      return false;
    }
    const { token, linkId } = config;

    if (!token || !linkId) {
      logError(AppLinkRedirectErrorMessage.MISSING_COOKIES, null);
      return false;
    }

    const tokenExpireDate = ELSTokenHelper.getExpirationDateFromToken(token);
    const now = moment();
    if (now.isAfter(tokenExpireDate)) {
      logError(AppLinkRedirectErrorMessage.EXPIRED_TOKEN, null);
      return false;
    }

    const tokenUser: ELSTokenUser = ELSTokenHelper.getUserInfoFromToken(token);
    if (isEmpty(tokenUser)) {
      logError(AppLinkRedirectErrorMessage.INVALID_TOKEN, null);
      return false;
    }

    const validRoles = [
      ELSCommonUIConstants.userType.Student,
      ELSCommonUIConstants.userType.Instructor
    ];

    if (!validRoles.includes(tokenUser.role)) {
      logError(AppLinkRedirectErrorMessage.UNSUPPORTED_USER_ROLE, null);
      return false;
    }

    return true;
  };

  useEffect(() => {
    setAppState((prevState) => {
      return {
        ...DefaultAppState,
        appLinkCookies: prevState.appLinkCookies,
        cookieCheckComplete: prevState.cookieCheckComplete,
        messages: prevState.messages,
      };
    });

    const linkHash = getQueryParam(LINK_HASH_QUERY_PARAM);

    if (linkHash) {
      fetchHashLinkAction({
        linkHash,
        appStateRef,
        setAppState
      }).then((appLinkCookies: AppLinkCookies) => {
        setAppState((prevState: AppState): AppState => {
          return {
            ...prevState,
            appLinkCookies,
          };
        });
        launchApp(appLinkCookies);
      }).catch((error) => {
        logError(AppLinkRedirectErrorMessage.REQUEST_FAILED, error);
      });
    } else if (isRedirectValid(appState.appLinkCookies)) {
      launchApp(appState.appLinkCookies);
    }
  }, []);

  if (state.hasError) {
    return (
      <div className="u-els-padding-3x">
        <h2 className="c-els-heading c-els-heading--h4 u-els-color-primary">{state.errorMessage}</h2>
      </div>
    );
  }

  return (
    <div className="u-els-padding-3x">
      <h2 className="c-els-heading c-els-heading--h4 u-els-color-primary"><ELSPageLoader /></h2>
    </div>
  );
};

const enhance = flowRight(
  withHTMLHeadSEO({ title: 'App Link Redirect' }),
);

const AppLinkRedirect = enhance(AppLinkRedirectComponent);

export default AppLinkRedirect;
